import { Wallet } from "lucide-react";
import type { EnergyFigures, EnergyHistory } from "@spf/contracts";
import { Card } from "../Card.js";
import { Failed, Loading } from "../ViewState.js";
import { cn } from "../../lib/cn.js";
import { coverageNote } from "../../lib/energy.js";
import { formatAmount, formatMoney } from "../../lib/format.js";
import type { Async } from "../../lib/useAsync.js";
import type { EnergyResult } from "../../lib/useEnergy.js";

/**
 * What the grid has cost today, in the design's Earning panel, and how much of
 * that went into charging the battery -- the two figures `REQUIREMENTS.md`
 * section 1 job 2 makes the headline of v1.
 *
 * Priced on the server, each quarter hour at the tariff in force when it
 * started (docs/DECISIONS/0033). With no tariff covering the day it says so
 * rather than showing a zero: 0.00 on a house that drew from the grid all day
 * is a wrong answer, not an empty one, the same reason `DECISIONS/0010`
 * refused a zero for solar.
 *
 * This is a **cost**, where the design says earning: nothing here exports to
 * the grid, so the money moves the other way.
 */
export function EarningWidget({
  today: { state: today, retry },
  currency,
  onExpand,
  className,
}: {
  today: EnergyResult;
  currency: string;
  onExpand?: () => void;
  className?: string;
}) {
  return (
    <Card
      onExpand={onExpand}
      title="Grid cost"
      className={cn("flex flex-col justify-between", className)}
    >
      {/* Wraps rather than overflowing: at 1024 the card is a quarter of the
          grid, too narrow for the icon and a five-figure amount side by side,
          and the card clips what does not fit. */}
      <div className="mt-2 flex flex-wrap items-end justify-between gap-4 pt-2">
        <div className="flex shrink-0 items-center justify-center rounded-2xl bg-line/60 p-3">
          <Wallet size={36} aria-hidden="true" className="text-accent" />
        </div>

        <div className="min-w-0 grow basis-44 text-right">
          <Cost today={today} currency={currency} onRetry={retry} />
        </div>
      </div>
    </Card>
  );
}

function Cost({
  today,
  currency,
  onRetry,
}: {
  today: Async<EnergyHistory>;
  currency: string;
  onRetry: () => void;
}) {
  if (today.kind === "loading") return <Loading label="Loading today’s cost…" />;
  if (today.kind === "error") return <Failed message={today.message} onRetry={onRetry} />;

  const { totals } = today.value;
  if (totals.recordedSeconds === 0) {
    return <Figure value="—" currency={currency} note="Nothing has been recorded today yet." />;
  }
  if (totals.gridImportCost === null) {
    return (
      <Figure
        value="—"
        currency={currency}
        note="No tariff covers today, so there is no rate to price it at. An administrator can add one under Settings."
      />
    );
  }

  const caveat = coverageNote(totals);
  return (
    <Figure
      value={formatAmount(totals.gridImportCost)}
      currency={currency}
      note={`${chargeShare(totals, currency)}${caveat === null ? "" : ` ${caveat}.`}`}
    />
  );
}

/** How much of the day's cost was the grid charging the battery. */
function chargeShare(totals: EnergyFigures, currency: string): string {
  if (totals.gridChargeCost === null) {
    return "Imported today. How much of it charged the battery is not known.";
  }
  if (totals.gridChargeCost === 0) return "Imported today. None of it charged the battery.";
  return `Imported today, ${formatMoney(totals.gridChargeCost, currency)} of it charging the battery.`;
}

function Figure({ value, currency, note }: { value: string; currency: string; note: string }) {
  return (
    <>
      <p className="tabular text-2xl font-bold tracking-tight text-ink sm:text-3xl">
        {value} <span className="text-xs font-normal text-ink-muted sm:text-sm">{currency}</span>
      </p>
      <p className="mt-1 text-xs font-medium text-ink-muted">{note}</p>
    </>
  );
}
